import {FC, useEffect, useState} from 'react'
import classNames from 'classnames'
import {useSelector} from 'react-redux'
import {
  selectedEndpointDebugMode,
  selectEndpoints,
} from 'features/Endpoints/endpointsSelectors'
import {dispatch} from 'app/store'
import {endpointsActions} from 'features/Endpoints/endpointsSlice'
import {HealthStatus, SliceStatus} from 'interfaces'
import {useActions} from 'hooks'
import {useTranslation} from 'react-i18next'
import {LoadingSpinner, RenderIf, RouterPrompt} from 'components/Basic'
import {colorCodes} from 'definitions/constant'
import {ActionContainer} from 'components/Layouts'
import {Button} from 'design'
import {CanIAccess, PERMISSIONS} from 'rbac'

interface DebugMessageComponentProps {
  message: string
}

export const DebugMessageComponent: FC<DebugMessageComponentProps> = ({
  message,
}) => <p className="text-white karla-bold text-sm mr-6">{message}</p>

export const EndpointsFooter: FC = () => {
  const {selectedEndpoints, items, status} = useSelector(selectEndpoints)
  const [debugModeAPI, setDebugModeAPI] = useState(false)
  const [endpointDisableAPI, setEndpointDisableAPI] = useState(false)
  const [showEndpointDisablePopup, setShowEndpointDisablePopup] =
    useState<boolean>(false)
  const {isSnackbarActionAvailable} = useActions()
  const {t} = useTranslation(['common', 'endpoints'])

  const isDebugMode = useSelector(selectedEndpointDebugMode)
  const isSelectedEndpointDisabled =
    selectedEndpoints?.status === HealthStatus.Disabled
  const onDisableDebugMode = () => {
    if (selectedEndpoints) {
      dispatch(
        endpointsActions.toggleEndpoint({
          debugMode: false,
          agentUUID: selectedEndpoints?.agentUUID || '',
          userUUID: selectedEndpoints?.userUUID || '',
        }),
      )
      dispatch(endpointsActions.stopDiagnosticPolling())
      setDebugModeAPI(true)
    }
  }

  const onDisableEndpointClick = (): void => {
    setShowEndpointDisablePopup(true)
  }

  const onDisableEndpointOkClick = (): void => {
    setShowEndpointDisablePopup(false)
    onToggleEndpoint()
  }

  const onDisableEndpointCancelClick = (): void => {
    setShowEndpointDisablePopup(false)
  }

  const onToggleEndpoint = () => {
    if (selectedEndpoints) {
      dispatch(
        endpointsActions.toggleEndpoint({
          disableOption: !isSelectedEndpointDisabled,
          agentUUID: selectedEndpoints?.agentUUID || '',
          userUUID: selectedEndpoints?.userUUID || '',
        }),
      )
      setEndpointDisableAPI(true)
    }
  }

  useEffect(() => {
    if (status === SliceStatus.resolved && debugModeAPI) {
      const data = {
        ...selectedEndpoints,
        debugMode: false,
        debugLevel: 0,
      }
      setDebugModeAPI(false)
      dispatch(endpointsActions.setSelectedEndpoint(data))
      dispatch(endpointsActions.resetEndpointDiagnostic())

      const foundIndex = items.findIndex(x => x.agentUUID == data.agentUUID)
      const endpointList = [...items]
      endpointList[foundIndex] = data
      dispatch(endpointsActions.updateEndpointList(endpointList))
    }

    if (status === SliceStatus.resolved) {
      setEndpointDisableAPI(false)
    }
  }, [status])

  return (
    <ActionContainer
      className={classNames(
        'border-t-0.5 border-gray-400 flex justify-end items-center',
        {
          'bg-dope-teal-200': isDebugMode,
          'bg-dope-black': !isDebugMode,
          hidden: !isSnackbarActionAvailable,
        },
      )}
    >
      <CanIAccess
        reqPermissions={[PERMISSIONS.ENABLE_DEBUG.WRITE]}
        fallbackComponent={
          <DebugMessageComponent
            message={t('common:DEBUG_MODE_FOOTER_MESSAGE_WITHOUT_CLICK')}
          />
        }
      >
        <>
          <DebugMessageComponent
            message={t('common:DEBUG_MODE_FOOTER_MESSAGE')}
          />

          <Button
            className="mr-4 w-40 h-8"
            size="xsm"
            fontWeight="bold"
            borderRadius="rounded"
            data-cy="exit-debug-mode"
            aria-label={t('common:EXIT_DEBUG_MODE')}
            onClick={onDisableDebugMode}
          >
            {!debugModeAPI ? (
              t('common:EXIT_DEBUG_MODE')
            ) : (
              <LoadingSpinner
                type="TailSpin"
                color={colorCodes.dopeBlack}
                width={16}
                height={16}
              />
            )}
          </Button>
        </>
      </CanIAccess>
      <CanIAccess reqPermissions={[PERMISSIONS.DISABLE_ENDPOINT.WRITE]}>
        <Button
          className="mr-3 h-8"
          colorScheme="cyan"
          size="xsm"
          borderRadius="rounded"
          fontWeight="bold"
          onClick={onDisableEndpointClick}
          data-cy="toggle-endpoint-bottom-panel"
        >
          <RenderIf isTrue={!endpointDisableAPI}>
            {isSelectedEndpointDisabled
              ? t('common:ENABLE_ENDPOINT')
              : t('common:DISABLE_ENDPOINT')}
          </RenderIf>
          <RenderIf isTrue={endpointDisableAPI}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.cyanBright}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </CanIAccess>
      <RenderIf isTrue={showEndpointDisablePopup}>
        <RouterPrompt
          title={
            isSelectedEndpointDisabled
              ? t('endpoints:ENDPOINT_ENABLE_CONFIRMATION_MESSAGE')
              : t('endpoints:ENDPOINT_DISABLE_CONFIRMATION_MESSAGE')
          }
          onCancel={onDisableEndpointCancelClick}
          onOK={onDisableEndpointOkClick}
        />
      </RenderIf>
    </ActionContainer>
  )
}
